import React, { useEffect, useRef, useContext } from 'react'
import mapboxgl, { Marker as MapboxMarkerType } from 'mapbox-gl'
import mapIcons, { MapIcon } from '../map/icons'
import { MapContext } from './mapContext'

export interface CustomIconMapbox {
  path: string
  fillOpacity: number
  fillColor: string
  strokeOpacity: number
  strokeWeight: number
  strokeColor: string
  scale: number
}

export interface LabelMapbox {
  text: string
  color?: string
  fontSize?: number
  fontWeight?: string
}

export interface MarkerProps {
  position: mapboxgl.LngLatLike
  scale?: number
  icon?: MapIcon
  title?: string
  color?: string
  borderColor?: string
  customIcon?: CustomIconMapbox
  label?: LabelMapbox
  onClick?: (event: mapboxgl.MapMouseEvent, marker: MapboxMarkerType) => void
  onDrag?: (event: mapboxgl.MapMouseEvent, marker: MapboxMarkerType) => void
  onDragEnd?: (event: mapboxgl.MapMouseEvent, marker: MapboxMarkerType) => void
  onDragStart?: (event: mapboxgl.MapMouseEvent, marker: MapboxMarkerType) => void
}

export default function Marker({
  position,
  icon = 'pin',
  scale = 1,
  color = mapIcons[icon].preferredColor,
  customIcon,
  label,
  onClick,
  onDrag,
  onDragEnd,
  onDragStart,
}: MarkerProps) {
  const markerRef = useRef<MapboxMarkerType | null>(null)
  const mapContext = useContext(MapContext)

  if (!mapContext) {
    throw new Error('Marker must be used within a Map')
  }

  const { map } = mapContext

  useEffect(() => {
    if (!map) {
      return
    }
    let marker: mapboxgl.Marker
    if (customIcon) {
      const element = document.createElement('div')
      element.className = 'custom-marker'
      element.style.width = `${customIcon?.scale * 2 || 24}px`
      element.style.height = `${customIcon?.scale * 2 || 24}px`
      element.style.backgroundColor = customIcon?.fillColor || '#007cbf'
      element.style.borderRadius = '50%'
      element.style.position = 'absolute'
      element.style.display = 'flex'
      element.style.justifyContent = 'center'
      element.style.alignItems = 'center'
      element.style.color = label?.color || '#fff'
      element.style.fontSize = `${label?.fontSize || 12}px`
      element.style.fontWeight = label?.fontWeight || 'bold'
      element.style.transform = 'translate(-50%, -50%)'

      if (label) element.innerText = label?.text

      marker = new mapboxgl.Marker({
        element,
        draggable: onDrag || onDragEnd || onDragStart ? true : false,
      })
        .setLngLat(position)
        .addTo(map)
    } else {
      marker = new mapboxgl.Marker({ color: color, scale: scale }).setLngLat(position).addTo(map)
    }

    if (onClick) {
      marker.getElement().addEventListener('click', event => {
        onClick(event as unknown as mapboxgl.MapMouseEvent, marker)
      })
    }
    if (onDrag) {
      marker.on('drag', event => {
        onDrag(event as mapboxgl.MapMouseEvent, marker)
      })
    }
    if (onDragEnd) {
      marker.on('dragend', event => {
        onDragEnd(event as mapboxgl.MapMouseEvent, marker)
      })
    }
    if (onDragStart) {
      marker.on('dragstart', event => {
        onDragStart(event as mapboxgl.MapMouseEvent, marker)
      })
    }

    markerRef.current = marker

    return () => {
      marker.remove()
    }
  }, [map, position, icon, color, scale, onClick, onDrag, onDragEnd, onDragStart])

  return null
}
